import {
  isSmartCollectionInputWithSlug,
  SmartCollectionResponse,
} from 'api/product/getSmartCollection/types';
import { useConfig } from 'config/use-config';
import { CollectionSorting } from 'constants/collection';
import { PetTag } from 'constants/product';
import { isCatSegmentation, SegmentationIds } from 'constants/segmentation';
import { useNinetailedAbTestContext } from 'contexts/ninetailed-ab-test';
import { useSegmentationContext } from 'contexts/segmentation';
import { useUpsellProducts } from 'hooks/product/use-upsell-product';
import { useRegion } from 'hooks/use-region';
import {
  CatNutritionalNeedsTags,
  FilterConfig,
  NutritionalNeedsTags,
} from 'interfaces/collection-filter';
import orderBy from 'lodash/orderBy';
import { CollectionProduct } from 'models/collection/collection';
import { Collection, transformCollection } from 'models/collection/transformCollection';
import { useRouter } from 'next/router';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SWRConfiguration } from 'swr';
import { isBundleProduct } from 'utils/product/is-bundle-product';
import { slugify } from 'utils/strings';
import useApplyFilterFromUrl from '../hooks/use-apply-filter-from-url';
import useCollectionFetch from '../hooks/use-collection-fetch';
import useRequestUrlUpdate from '../hooks/use-request-url-update';
import { CollectionPropTypes } from '../types/CollectionPropTypes';
import getAllFilter from '../utils/get-all-filter';
import { modifyConfig } from '../utils/modify-config/utils';
import { useVisibleProductList } from './visible-products';
import { useRootStore } from 'stores/use-root-store';
import { stripRegionPathPrefix } from 'utils/routing/strip-region-path-prefix';
import { PetType } from 'constants/pet-type';

interface PropTypeBase {
  collection?: SmartCollectionResponse;
  sortBy?: CollectionSorting;
  tags?: string[];
  children: React.ReactNode;
  swrOptions?: SWRConfiguration;
  /** Name given to impression tracker */
  trackingListName?: string;
  /** Helper to propagate tags to parent component  */
  setUsedTags?: React.Dispatch<React.SetStateAction<string[]>>;
  applyPetProfile?: boolean;
  couponCode?: string;
}

type PropTypes = CollectionPropTypes<PropTypeBase>;

interface CollectionContextTypes {
  activeAllergyFilter: string[];
  appliedAllergyFilter: string[];
  appliedFilter: string[];
  collection: Collection;
  filterConfig: FilterConfig | undefined;
  isValidating: boolean;
  limitOfProductsToShow?: number;
  sorting: CollectionSorting;
  visibleProducts: CollectionProduct[];
  resetFilter: () => void;
  setLimitOfProductsToShow: React.Dispatch<React.SetStateAction<number | undefined>>;
  toggleActiveAllergyFilter: (value: string) => void;
  toggleAllergyFilter: (value: string) => void;
  toggleFilter: (value: string) => void;
  toggleFilters: (values: string[]) => void;
  /** The length of products, which is independent of limitOfProductsToShow */
  productsLength: number;
  /** Name given to impression tracker, if not specified, product handle could be given */
  trackingListName?: string;
}

export const CollectionContext = React.createContext<CollectionContextTypes>(
  {} as CollectionContextTypes
);

/**
 * Map of filter to segmentation
 */
const SEGMENTS_FILTER_MAP: Partial<{
  [key in NutritionalNeedsTags | CatNutritionalNeedsTags]: SegmentationIds | Array<SegmentationIds>;
}> = {
  'haut-and-fellprobleme': 'skin-fur',
  'sehr-wahlerisch': [SegmentationIds.PickyEater, SegmentationIds.CatPickyEater],
  'senior-7-jahre': 'senior',
  'unvertraglichkeiten-allergien': 'intolerance',
  verdauungsprobleme: [SegmentationIds.Digestion, SegmentationIds.CatDigestion],
  welpe: 'welpe',
  ubergewicht: [SegmentationIds.Overweight, SegmentationIds.CatOverweight],
  kitten: SegmentationIds.Kitten,
  senior: SegmentationIds.CatSenior,
};

/**
 * Filter value set for segmentation
 */
const SEGMENT_FILTERS_SET = new Set(Object.keys(SEGMENTS_FILTER_MAP));

const sortCollection = (collection: Collection, sorting: CollectionSorting): Collection => {
  let sortedProducts = collection.products;

  if (sorting === CollectionSorting.TITLE) {
    sortedProducts = orderBy(collection.products, (product) => product.title.trim(), 'asc');
  }

  if ([CollectionSorting.PRICE_ASCENDING, CollectionSorting.PRICE_DESCENDING].includes(sorting)) {
    sortedProducts = orderBy(
      collection.products,
      (product) => parseFloat(product.variants[0].priceV2.amount),
      sorting === CollectionSorting.PRICE_ASCENDING ? 'asc' : 'desc'
    );
  }

  if (sorting === CollectionSorting.NEW) {
    sortedProducts = orderBy(collection.products, (product) => new Date(product.createdAt), 'asc');
  }

  if (sorting === CollectionSorting.BEST_SELLING) {
    sortedProducts = orderBy(collection.products, () => 0, 'asc');
  }

  return { ...collection, products: sortedProducts };
};

/**
 * Collection Context provider.
 * Filter values should be updated via only url updates.
 * so that always the flow is streamlined in url -> appliedFilter.
 *
 * This context accepts either collection handle, segmentation store or dynamic collection input
 *
 * @TODO : trackingListName is provisional implementation. waiting the answer from chris
 */
export const CollectionProvider: React.FC<PropTypes> = ({
  children,
  tags: initialTags = [],
  collection: collectionDefault,
  sortBy,
  swrOptions = {},
  trackingListName,
  setUsedTags,
  applyPetProfile,
  couponCode,
  ...others
}) => {
  const petProfile = useRootStore((store) => store.currentPetProfile);
  const { shopId, commonCollections } = useConfig();
  const { collectionHandle, smartCollectionInput } = others;
  const { experimentHeaderStr, audiencesHeaderStr } = useNinetailedAbTestContext();

  // in case collectionHandle is snack request useUpsellProducts, otherwise skip it
  const skipUpsale =
    petProfile.petType === 'dog'
      ? collectionHandle !== commonCollections.dogsSnacks
      : collectionHandle !== commonCollections.catsSnacks;

  const { rawResponse, upsellProducts } = useUpsellProducts({
    fallbackSlug: collectionHandle,
    skip: skipUpsale,
    maxItemsInResult: 20,
  });

  // make smart collection response for CollectionProvider
  const upsellCollection: SmartCollectionResponse | undefined | null = useMemo(() => {
    if (rawResponse && upsellProducts.length > 0) {
      return { ...rawResponse, items: upsellProducts };
    }
  }, [rawResponse, upsellProducts]);

  const ninetailed = useMemo(() => experimentHeaderStr?.split(','), [experimentHeaderStr]);
  // skip useCollectionFetch request when we need to request upSale products instead
  const { data: fetchData, isLoading } = useCollectionFetch({
    swrOptions,
    ninetailed,
    // Remove EC audience from smartCollectionInput
    // so that we can apply control experience for EC customer experience
    audiences: audiencesHeaderStr?.split(','),
    couponCode,
    ...(collectionDefault ? { collectionDefault } : {}),
    // others alias is used to keep the type assertion
    ...others,
  });

  const data = !skipUpsale ? upsellCollection : fetchData;

  const petType = useRootStore((store) => store.petType);
  const router = useRouter();
  const isCDP = stripRegionPathPrefix(router.pathname) === '/collections/[...all]';

  const {
    segmentation: _segmentation,
    shouldOmitPersonalization,
    pushSegmentation,
  } = useSegmentationContext();

  const [limitOfProductsToShow, setLimitOfProductsToShow] = useState<number>();

  const transformedCollection = React.useMemo(() => {
    if (!data) {
      return transformCollection({
        title: '',
        collection: {
          title: '',
          products: [],
        },
        shopId,
      });
    }

    return transformCollection({
      collection: data,
      shopId,
    });
  }, [data, shopId]);

  // TODO: why do we need this if we have petType already?
  const petTypeFromCollectionTags = useMemo(() => {
    const isDog = transformedCollection.products.some((p) => p.tags.indexOf(PetTag.Dog) > -1);
    return isDog ? 'dog' : 'cat';
  }, [transformedCollection.products]);

  const region = useRegion();

  const filterConfig = useMemo(() => {
    let handle = collectionHandle;

    if (!handle && smartCollectionInput && isSmartCollectionInputWithSlug(smartCollectionInput)) {
      handle = smartCollectionInput.slug;
    }
    // apply all available filters when handle is not available
    if (!handle) {
      return getAllFilter({ petType: petTypeFromCollectionTags });
    }

    return modifyConfig({
      filterConfig: getAllFilter({ petType: petTypeFromCollectionTags }),
      isPetProfileApplied: applyPetProfile,
      handle,
      region,
      petType: petTypeFromCollectionTags,
    });
  }, [
    applyPetProfile,
    collectionHandle,
    petTypeFromCollectionTags,
    region,
    smartCollectionInput,
  ]) as CollectionContextTypes['filterConfig'];

  const [sorting, setSorting] = useState<CollectionSorting>(CollectionSorting.DEFAULT);

  const collection = useMemo(() => {
    return sortCollection(transformedCollection, sorting);
  }, [transformedCollection, sorting]);

  useEffect(() => {
    setSorting(sortBy || CollectionSorting.DEFAULT);
  }, [sortBy, data]);

  /**
   * Filter Logic
   */
  const [appliedFilter, setAppliedFilter] = useState<string[]>(
    initialTags.map((tag: string) => slugify(tag))
  );

  const [appliedAllergyFilter, setAppliedAllergyFilter] = useState<string[]>([]);

  // Since the allregy filter also include a value that is toggled in the standard way,
  // we need to keep track of the active allergy filter separately.
  const [activeAllergyFilter, setActiveAllergyFilter] = useState<string[]>([]);
  const toggleActiveAllergyFilter = useCallback(
    (value: string) => {
      const newFilter = activeAllergyFilter.includes(value)
        ? activeAllergyFilter.filter((filter) => filter !== value)
        : [...activeAllergyFilter, value];
      setActiveAllergyFilter(newFilter);
    },
    [activeAllergyFilter]
  );

  /**
   * Apply filter values via url params.
   */
  useApplyFilterFromUrl({
    setAppliedFilter,
    isReady: router.isReady,
    targetQuery: router.query['all'],
  });

  /** Propagate used tags to parent  */
  useEffect(() => {
    if (setUsedTags) {
      setUsedTags([...appliedFilter]);
    }
  }, [appliedFilter, setUsedTags]);

  const visibleProducts = useVisibleProductList({
    collection: collection,
    activeFilter: appliedFilter,
    activeAllergiesFilter: appliedAllergyFilter,
    filterConfig: filterConfig,
    segmentation: _segmentation,
    shouldOmitPersonalization: shouldOmitPersonalization,
  });

  /**
   * Due to the time lag between url update and applied filter update,
   * applied filter are previous values even after url is updated.
   * so here, we reset values when it's a non-shallow route change
   * */
  useEffect(() => {
    const handleRouteChange = (_, { shallow }): void => {
      if (!shallow) {
        setAppliedFilter([]);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router]);

  const handle = useMemo(() => {
    try {
      if (collectionHandle) {
        return collectionHandle;
      }
      return router.isReady && router.query.all?.[0] ? router.query.all[0] : undefined;
    } catch (error) {
      return undefined;
    }
  }, [collectionHandle, router.isReady, router.query.all]);

  const { requestURLUpdate } = useRequestUrlUpdate({
    baseUri: `/collections/${handle}`,
    excludeKey: 'all',
  });

  const initialFilterClick = useRef(false);

  /**
   * Toggles a filter value and updates applied filters and segmentation.
   * Updates applied filters and segmentation based on whether the filter value is added or removed.
   * For Collection pages, it updates the URL; otherwise, it updates applied filters directly.
   */
  const toggleFilter = useCallback(
    (value: string) => {
      const newFilter = appliedFilter.includes(value)
        ? appliedFilter.filter((filter) => filter !== value)
        : [...appliedFilter, value];

      initialFilterClick.current = true;
      isCDP ? requestURLUpdate(newFilter) : setAppliedFilter(newFilter);
    },
    [appliedFilter, isCDP, requestURLUpdate]
  );

  /**
   * Toggles multiple filter values and updates applied filters and segmentation.
   * Updates applied filters and segmentation based on whether the filter values are added or removed.
   * For Collection pages, it updates the URL; otherwise, it updates applied filters directly.
   */
  const toggleFilters = useCallback(
    (values: string[]) => {
      // Create a new filter array by toggling each value
      let newFilter = [...appliedFilter];

      values.forEach((value) => {
        if (newFilter.includes(value)) {
          // Remove if already present
          newFilter = newFilter.filter((filter) => filter !== value);
        } else {
          // Add if not present
          newFilter = [...newFilter, value];
        }
      });

      initialFilterClick.current = true;
      isCDP ? requestURLUpdate(newFilter) : setAppliedFilter(newFilter);
    },
    [appliedFilter, isCDP, requestURLUpdate]
  );

  /**
   * Check logics here
   */
  useEffect(() => {
    const filtered = appliedFilter.filter((v) => SEGMENT_FILTERS_SET.has(v));
    if (filtered.length > 0) {
      const segmentFilter = SEGMENTS_FILTER_MAP[filtered[filtered.length - 1]];
      if (typeof segmentFilter === 'string') {
        pushSegmentation({ segmentation: segmentFilter });
      } else if (Array.isArray(segmentFilter) && petType) {
        const segmentation = (segmentFilter as Array<SegmentationIds>).find((filter) => {
          if (petType === PetType.Dog) {
            return !isCatSegmentation(filter);
          } else {
            return isCatSegmentation(filter);
          }
        });
        segmentation &&
          pushSegmentation({
            segmentation: segmentation,
            isUserAction: true,
          });
      }
    }
  }, [appliedFilter, collectionHandle, petType, pushSegmentation]);

  const toggleAllergyFilter = useCallback(
    (value: string) => {
      const newFilter = appliedAllergyFilter.includes(value)
        ? appliedAllergyFilter.filter((filter) => filter !== value)
        : [...appliedAllergyFilter, value];
      setAppliedAllergyFilter(newFilter);
    },
    [appliedAllergyFilter]
  );

  const resetFilter = useCallback(() => {
    isCDP ? requestURLUpdate([]) : setAppliedFilter([]);
    setActiveAllergyFilter([]);
    setAppliedAllergyFilter([]);
  }, [isCDP, requestURLUpdate]);

  // If the products are not displayed in the CDP, we need to filter out the
  // Purpose boxes.
  const filteredVisibleProducts = useMemo(
    () =>
      isCDP ? visibleProducts : visibleProducts.filter((product) => !isBundleProduct(product)),
    [isCDP, visibleProducts]
  );

  return (
    <CollectionContext.Provider
      value={{
        activeAllergyFilter,
        appliedAllergyFilter,
        appliedFilter,
        collection,
        filterConfig,
        isValidating: isLoading,
        limitOfProductsToShow,
        sorting,
        visibleProducts: filteredVisibleProducts,
        resetFilter,
        setLimitOfProductsToShow,
        toggleActiveAllergyFilter,
        toggleAllergyFilter,
        toggleFilter,
        toggleFilters,
        productsLength: visibleProducts.length,
        trackingListName: trackingListName ?? handle ?? 'list not identified',
      }}
    >
      {children}
    </CollectionContext.Provider>
  );
};

/** Provide collection context  */
export const useCollectionState = () => useContext(CollectionContext);
